/**
 * MONATSPAUSCHALE — der MONATSVORSCHLAG und die MONATSBUCHUNG, die Regeln.
 *
 * openFlatMonths (flatRateLogic) sagt bereits, welche Monate offen sind.
 * Dieses Modul baut daraus den VORSCHLAG fuer die Oberflaeche ("08/2026 noch
 * nicht gebucht — buchen") und plant die BUCHUNG: ein Protokolleintrag auf der
 * Pauschalposition mit description 'Monatspauschale MM/YYYY' und
 * bookingReference 'FLAT-YYYY-MM'.
 *
 * Die drei Regeln, alle vom Betreiber entschieden:
 *  1. Der Vorschlag ist 1 Monat, die Menge bleibt FREI ueberschreibbar (0,5
 *     fuer einen angebrochenen Monat). Der Monat bleibt trotzdem nur EINMAL
 *     buchbar — der SCHLUESSEL ist der Riegel, nicht die Menge.
 *  2. Ein Monat OHNE Begehung wird trotzdem vorgeschlagen: Kalender und
 *     Laufzeit entscheiden, nie das Protokoll (die Regel steckt schon in
 *     openFlatMonths, hier wird sie nur durchgereicht).
 *  4. NIE still automatisch buchen: dieses Modul plant nur; gebucht wird auf
 *     Klick, und die Schreibpfade (useReportActions bzw.
 *     flatRateBookingService) rufen planFlatMonthBooking ERNEUT gegen den
 *     frischen Stand — ein veralteter Dialog kann so keinen inzwischen
 *     gebuchten Monat doppelt fordern.
 *
 * Pure Funktionen wie flatRateLogic daneben: kein Supabase, kein new Date(),
 * "heute" und jede Kennung werden hereingereicht (kein jsdom im Projekt, nur
 * so ist das testbar). Geschrieben wird woanders.
 */

import type { ServiceItem, ServiceTransaction } from '../types';
import {
    bookedFlatMonths,
    flatKeyLabel,
    flatKeyServiceDate,
    isFlatRateService,
    openFlatMonths,
} from './flatRateLogic';

/** Der Projektstand, aus dem Vorschlag und Buchung rechnen. Alles hereingereicht. */
export interface FlatMonthProjectState {
    /** Positionsliste (globalData.services bzw. getNormalizedServices). */
    services: ServiceItem[];
    /** Protokoll — nur fuer die bereits verbrauchten FLAT-Schluessel. */
    serviceLog: Array<{ serviceId?: string; bookingReference?: string }> | null | undefined;
    /** Projektlaufzeit (globalData.startDate / endDate). */
    startDate?: string | null;
    endDate?: string | null;
    /** Heutiger Tag (ISO), hereingereicht statt new Date() — testbar. */
    today: string;
}

/** EIN offener Monat EINER Pauschalposition — eine Zeile des Vorschlags. */
export interface FlatMonthSuggestion {
    flatServiceId: string;
    /** Anzeige der Position: 'LV-Nr. Titel' (wie im Umstellungs-Assistenten). */
    flatName: string;
    /** Buchungsschluessel 'FLAT-YYYY-MM' — der Riegel. */
    monthKey: string;
    /** Anzeige 'MM/YYYY'. */
    monthLabel: string;
}

const nameOf = (s: ServiceItem): string => {
    const title = (s.title || '').trim() || s.id;
    const lv = (s.lvPosition || '').trim();
    return lv ? `${lv} ${title}` : title;
};

/**
 * Alle offenen Monate aller Pauschalpositionen, je Position in zeitlicher
 * Reihenfolge (LV-Reihenfolge der Positionen zuerst — dieselbe Rangfolge wie
 * resolveFlatCoverage, damit die Anzeige deterministisch ist).
 *
 * Der Vorschlag haengt am KALENDER (Betreiber-Entscheidung 2): das Protokoll
 * geht nur ueber die bereits verbrauchten FLAT-Schluessel der Pauschale selbst
 * ein — auch die Monatsverschluesse der Umstellung (Menge 0) zaehlen dort als
 * verbraucht, genau dafuer sind sie da. Projekte ohne Pauschalposition oder
 * ohne lesbare Laufzeit ergeben eine leere Liste (openFlatMonths: lieber kein
 * Vorschlag als einer aus geratenen Monaten).
 */
export const suggestFlatMonths = (state: FlatMonthProjectState): FlatMonthSuggestion[] => {
    const services = Array.isArray(state.services) ? state.services : [];
    const out: FlatMonthSuggestion[] = [];
    services.filter(isFlatRateService).forEach((flat) => {
        openFlatMonths({
            start: state.startDate,
            end: state.endDate,
            today: state.today,
            serviceLog: state.serviceLog,
            flatServiceId: flat.id,
        }).forEach((monthKey) => {
            const monthLabel = flatKeyLabel(monthKey);
            if (monthLabel === null) return;
            out.push({ flatServiceId: flat.id, flatName: nameOf(flat), monthKey, monthLabel });
        });
    });
    return out;
};

/**
 * Eindeutiger Auswahlwert einer Vorschlagszeile fuer die Oberflaeche.
 *
 * Der monthKey allein ist NICHT eindeutig: zwei Pauschalpositionen desselben
 * Projekts haben denselben offenen Monat (beide 'FLAT-2026-07'), und eine
 * Auswahl nur ueber den Monat traefe immer die ERSTE Position — gebucht
 * wuerde eine andere Pauschale, als die Auswahl anzeigt. Deshalb gehoeren
 * flatServiceId und monthKey ZUSAMMEN in den Wert; '|' kommt in keiner der
 * beiden Haelften vor (Positions-Id bzw. 'FLAT-YYYY-MM').
 */
export const flatSuggestionKey = (
    s: Pick<FlatMonthSuggestion, 'flatServiceId' | 'monthKey'>,
): string => `${s.flatServiceId}|${s.monthKey}`;

/** Die Vorschlagszeile zu einem Auswahlwert — null, wenn es sie nicht (mehr) gibt. */
export const findFlatSuggestion = (
    suggestions: FlatMonthSuggestion[],
    key: string,
): FlatMonthSuggestion | null =>
    suggestions.find((s) => flatSuggestionKey(s) === key) ?? null;

/** Was der Buchungsdialog abfragt. */
export interface FlatMonthBookingDraft {
    flatServiceId: string;
    /** Der zu buchende Monat als Schluessel 'FLAT-YYYY-MM'. */
    monthKey: string;
    /**
     * Menge — der Dialog belegt 1 vor, ueberschreibbar (Betreiber-
     * Entscheidung 1). Sie aendert den Schluessel NIE: 0,5 verbraucht den
     * Monat genauso.
     */
    quantity: number;
}

/** Kennungen und Zeitpunkt des Vorgangs — hereingereicht wie bei planWaiver. */
export interface FlatMonthProvision {
    /** Id des neuen Protokolleintrags (crypto.randomUUID im Aufrufer). */
    entryId: string;
    /** ISO-Zeitpunkt des Vorgangs. */
    now: string;
    actorName: string;
    actorId?: string;
}

export interface FlatMonthBookingPlan {
    /** Die vollstaendige Positionsliste MIT der fortgeschriebenen Pauschale. */
    services: ServiceItem[];
    /** Der Eintrag fuer globalData.serviceLog. */
    logEntry: ServiceTransaction;
    /** 'Monatspauschale MM/YYYY' — fuer Meldung und Erfolgsansage. */
    label: string;
}

/**
 * Bewusst ein FLACHES Ergebnis (dieselbe Begruendung wie FlatSwitchResult:
 * das Projekt uebersetzt ohne strictNullChecks, eine unterschiedene
 * Vereinigung wuerde dort nicht verengt).
 */
export interface FlatMonthBookingResult {
    ok: boolean;
    /** Nur bei `ok`. */
    plan?: FlatMonthBookingPlan;
    /** Nur bei `!ok` — Klartext fuer die Oberflaeche. */
    error?: string;
}

/**
 * Die Monatsbuchung planen. Schreibt nichts, entscheidet nur.
 *
 * Der Riegel gegen Doppelbuchung ist der SCHLUESSEL: traegt das Protokoll den
 * Monat schon (auch als Verschluss mit Menge 0), prallt die Buchung ab — mit
 * eigener Meldung, denn "schon gebucht" ist der erwartbare Wettlauf-Fall
 * (zwei offene Ansichten), kein Anlagefehler. Danach muss der Monat unter den
 * OFFENEN liegen (openFlatMonths): das haelt den laufenden Monat, Monate vor
 * dem Laufzeitbeginn und Monate nach dem Projektende heraus — ein Vorschlag,
 * den es nie gab, darf auch nicht buchbar sein.
 *
 * Die MENGE muss eine Zahl groesser 0 sein — KEIN Rueckfall auf 1 wie bei der
 * Einweisung: dort ist die 1 der dokumentierte Normalfall, hier waere sie
 * eine still gebuchte Menge, die der Bearbeiter so nie bestaetigt hat
 * (Betreiber-Entscheidung 4). Menge 0 ist ausserdem die Form des
 * VERSCHLUSSES (flatRateSwitchLogic) und keine Forderung.
 */
export const planFlatMonthBooking = (
    state: FlatMonthProjectState,
    draft: FlatMonthBookingDraft,
    provision: FlatMonthProvision,
): FlatMonthBookingResult => {
    const services = Array.isArray(state.services) ? state.services : [];
    const index = services.findIndex((s) => s.id === draft.flatServiceId);
    const flat = index === -1 ? null : services[index];
    if (!flat || !isFlatRateService(flat)) {
        // Auch die halb angelegte Pauschale (assignment ohne Deckung) faellt
        // hierher: sie soll auffallen und vervollstaendigt werden, nicht
        // Monatsbuchungen sammeln, die nichts decken.
        return {
            ok: false,
            error: 'Die Pauschalposition wurde nicht gefunden oder trägt keinen Deckungsumfang — es wurde nichts gebucht.',
        };
    }

    const monthLabel = flatKeyLabel(draft.monthKey);
    if (monthLabel === null) {
        return { ok: false, error: 'Der Monat ist kein gültiger Buchungsschlüssel — es wurde nichts gebucht.' };
    }

    if (bookedFlatMonths(state.serviceLog, flat.id).has(draft.monthKey)) {
        return {
            ok: false,
            error: `Der Monat ${monthLabel} ist auf dieser Pauschale bereits gebucht — jeder Monat nur einmal (der Schlüssel ist der Riegel, nicht die Menge). Es wurde nichts gebucht.`,
        };
    }

    const open = openFlatMonths({
        start: state.startDate,
        end: state.endDate,
        today: state.today,
        serviceLog: state.serviceLog,
        flatServiceId: flat.id,
    });
    if (!open.includes(draft.monthKey)) {
        return {
            ok: false,
            error: `Der Monat ${monthLabel} ist für diese Pauschale nicht offen (laufender Monat oder außerhalb der Projektlaufzeit) — es wurde nichts gebucht.`,
        };
    }

    if (!Number.isFinite(draft.quantity) || draft.quantity <= 0) {
        return { ok: false, error: 'Bitte eine Menge größer als 0 angeben (Vorschlag: 1 Monat; 0,5 für einen angebrochenen Monat).' };
    }

    // Fortschreiben wie jeder Buchungspfad (reportBooking/einweisungBooking):
    // quantityUsed steigt, 'done' beim Erreichen der Vertragsmenge; 0 Monate
    // heisst offene Laufzeit und wird nie 'done'.
    const newUsed = (flat.quantityUsed || 0) + draft.quantity;
    const isDone = flat.quantityContracted > 0 && newUsed >= flat.quantityContracted;
    const newServices = [...services];
    newServices[index] = { ...flat, quantityUsed: newUsed, status: isDone ? 'done' : flat.status };

    const logEntry: ServiceTransaction = {
        id: provision.entryId,
        // Datum der LEISTUNG = Monatsende des gebuchten Monats, nicht der Tag
        // des Klicks (der steht in bookedAt). Nur so findet der Zeitraumfilter
        // der Rechnung den Monat dort, wo er hingehoert — siehe
        // flatKeyServiceDate. Den Begehungsrhythmus beruehrt das nicht: der
        // haengt an metadata.date und an Positionen mit assignment
        // 'inspection', die Pauschale traegt 'flatrate'. Die Verschluesse der
        // Umstellung (Menge 0) behalten ihr Vorgangsdatum, sie sind keine
        // Leistung. Der Rueckfall auf `now` ist unerreichbar (der Schluessel
        // wurde oben geprueft) und nur der Typsicherheit geschuldet.
        date: flatKeyServiceDate(draft.monthKey) ?? provision.now,
        serviceId: flat.id,
        quantity: draft.quantity,
        performerId: provision.actorId || 'system',
        performerName: provision.actorName,
        isSubstitution: false,
        // Fester Wortlaut 'Monatspauschale MM/YYYY' (flatMonthLabel) — eine
        // Buchung ohne sprechende description hat hier schon einmal jede
        // spaetere Pruefung uebersehen (Archivabgleich-Regel).
        description: `Monatspauschale ${monthLabel}`,
        bookedAt: provision.now,
        bookedBy: provision.actorName,
        bookedById: provision.actorId,
        bookingReference: draft.monthKey,
    };

    return {
        ok: true,
        plan: { services: newServices, logEntry, label: `Monatspauschale ${monthLabel}` },
    };
};
